import { useAuth } from '@/lib/useAuth';
import { useState, useCallback, useEffect, useRef } from 'react';
import {
    Room,
    RoomEvent,
    Track,
    RemoteTrack,
    RemoteParticipant,
    LocalParticipant,
    ConnectionState,
    DisconnectReason,
    RemoteTrackPublication,
    LocalTrackPublication,
} from 'livekit-client';
import { toast } from 'react-toastify';

type LigacaoStatus = 'chamando' | 'iniciando' | 'em_andamento' | 'preencher_solucionado' | 'finalizado';

export type LigacaoState = {
    status: LigacaoStatus;
    startDate: Date | null;
    isMuted: boolean;
    isSpeakerOn: boolean;
    isMinimized: boolean;
    localAudioLevel: number;
    remoteAudioLevel: number;
}

export type LigacaoInfo = {
    participantKey?: string;
    roomName?: string;
    livekitToken?: string;
    livekitUrl?: string;
    canalClienteId: string;
    canalClienteNome: string;
    canalClienteImagem?: string;
}

export function useLigacaoLiveKit(websocket: any) {
    const [ligacao, setLigacao] = useState<LigacaoInfo | null>(null);
    const [ligacaoState, setLigacaoState] = useState<LigacaoState | null>(null);

    const roomRef = useRef<Room | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const audioAnalyserLocalRef = useRef<AnalyserNode | null>(null);
    const audioAnalyserRemoteRef = useRef<AnalyserNode | null>(null);
    const audioLevelIntervalRef = useRef<number | null>(null);
    const reconnectionTimeoutRef = useRef<number | null>(null);
    const connectionFailureCountRef = useRef<number>(0);
    const isTransferringRef = useRef<boolean>(false);

    const lastCallIdRef = useRef<string | null>(null);
    const lastParticipantKeyRef = useRef<string | null>(null);

    const { user } = useAuth();

    const _newLigacaoState = useCallback((status?: LigacaoStatus): LigacaoState => {
        return {
            status: status || 'iniciando',
            startDate: null,
            isMuted: false,
            isSpeakerOn: true,
            isMinimized: false,
            localAudioLevel: 0,
            remoteAudioLevel: 0,
        };
    }, []);

    const setupAudioAnalysers = useCallback((localStream?: MediaStream, remoteStream?: MediaStream) => {
        try {
            if (!localStream && !remoteStream) return;

            console.log('[LiveKit] 🎚️ Configurando analisadores de áudio...', {
                hasLocal: !!localStream,
                hasRemote: !!remoteStream
            });

            // Criar AudioContext se não existir
            if (!audioContextRef.current) {
                audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
            }

            const audioContext = audioContextRef.current;

            // Resumir AudioContext se estiver suspenso (necessário em alguns browsers)
            if (audioContext.state === 'suspended') {
                console.log('[LiveKit] 🎚️ Resumindo AudioContext...');
                audioContext.resume();
            }

            // Configurar analisador para o áudio local
            if (localStream && !audioAnalyserLocalRef.current) {
                console.log('[LiveKit] 🎚️ Configurando analisador LOCAL');
                const localSource = audioContext.createMediaStreamSource(localStream);
                audioAnalyserLocalRef.current = audioContext.createAnalyser();
                audioAnalyserLocalRef.current.fftSize = 256;
                localSource.connect(audioAnalyserLocalRef.current);
            }

            // Configurar analisador para o áudio remoto
            if (remoteStream && !audioAnalyserRemoteRef.current) {
                console.log('[LiveKit] 🎚️ Configurando analisador REMOTO');
                const remoteSource = audioContext.createMediaStreamSource(remoteStream);
                audioAnalyserRemoteRef.current = audioContext.createAnalyser();
                audioAnalyserRemoteRef.current.fftSize = 256;
                remoteSource.connect(audioAnalyserRemoteRef.current);
            }

            // Iniciar intervalo para medir níveis de áudio (sempre recriar se não existir)
            if (!audioLevelIntervalRef.current) {
                console.log('[LiveKit] 🎚️ Iniciando intervalo de medição de áudio');
                audioLevelIntervalRef.current = window.setInterval(() => {
                    // Medir nível de áudio local
                    if (audioAnalyserLocalRef.current) {
                        const dataArrayLocal = new Uint8Array(audioAnalyserLocalRef.current.frequencyBinCount);
                        audioAnalyserLocalRef.current.getByteFrequencyData(dataArrayLocal);
                        const averageLocal = dataArrayLocal.reduce((acc, val) => acc + val, 0) / dataArrayLocal.length;
                        const normalizedLocalLevel = Math.min(averageLocal / 128, 1);
                        setLigacaoState(prev => prev ? { ...prev, localAudioLevel: normalizedLocalLevel } : null);
                    }

                    // Medir nível de áudio remoto
                    if (audioAnalyserRemoteRef.current) {
                        const dataArrayRemote = new Uint8Array(audioAnalyserRemoteRef.current.frequencyBinCount);
                        audioAnalyserRemoteRef.current.getByteFrequencyData(dataArrayRemote);
                        const averageRemote = dataArrayRemote.reduce((acc, val) => acc + val, 0) / dataArrayRemote.length;
                        const normalizedRemoteLevel = Math.min(averageRemote / 128, 1);
                        setLigacaoState(prev => prev ? { ...prev, remoteAudioLevel: normalizedRemoteLevel } : null);
                    }
                }, 100);
            }
        } catch (error) {
            console.error('[LiveKit] Erro ao configurar analisadores de áudio:', error);
        }
    }, []);

    /**
     * Libera o microfone imediatamente - usado ao transferir ou encerrar ligação
     * para evitar conflitos de dispositivo entre abas/agentes
     */
    const liberarMicrofone = useCallback(async () => {
        if (!roomRef.current) return;

        console.log('[LiveKit] 🎤 Liberando microfone imediatamente...');
        try {
            // Desabilitar microfone
            await roomRef.current.localParticipant.setMicrophoneEnabled(false);

            // Parar todas as tracks de áudio para garantir liberação do dispositivo
            roomRef.current.localParticipant.audioTrackPublications.forEach(pub => {
                if (pub.track) {
                    pub.track.stop();
                    console.log('[LiveKit] 🎤 Track de áudio parada:', pub.trackSid);
                }
            });

            console.log('[LiveKit] 🎤 Microfone liberado com sucesso');
        } catch (err) {
            console.warn('[LiveKit] ⚠️ Erro ao liberar microfone:', err);
        }
    }, []);

    const iniciarLigacao = useCallback((chat: any) => {
        if (!chat) return;

        setLigacao({
            canalClienteId: chat.canal_cliente_id,
            canalClienteNome: chat.cliente?.nome || 'Cliente',
            canalClienteImagem: chat.cliente?.imagem || undefined,
        });

        setLigacaoState({
            status: 'iniciando',
            startDate: new Date(),
            isMuted: false,
            isSpeakerOn: true,
            isMinimized: false,
            localAudioLevel: 0,
            remoteAudioLevel: 0,
        });

        // Solicitar ao backend para iniciar a chamada
        websocket.emit('call.start', { chatId: chat.id });
    }, [websocket]);

    /**
     * Inicia uma chamada VoIP outbound (do atendente para um número externo)
     * 
     * @param params - Parâmetros da chamada outbound
     * @param params.canalId - ID do canal VoIP a usar
     * @param params.clienteId - ID do cliente (opcional)
     * @param params.clienteNome - Nome do cliente ou identificação
     * @param params.telefone - Número de telefone a discar
     */
    const iniciarChamadaOutbound = useCallback((params: {
        canalId: number;
        clienteId?: number;
        clienteNome: string;
        telefone: string;
    }) => {
        console.log('[LiveKit] 📞 Iniciando chamada outbound:', params);

        // Configurar estado da ligação antes de emitir o evento
        setLigacao({
            canalClienteId: params.telefone,
            canalClienteNome: params.clienteNome,
        });

        setLigacaoState({
            status: 'chamando',
            startDate: null,
            isMuted: false,
            isSpeakerOn: true,
            isMinimized: false,
            localAudioLevel: 0,
            remoteAudioLevel: 0,
        });

        // Emitir evento para o backend iniciar a chamada
        websocket.emit('call.outbound', {
            canalId: params.canalId,
            clienteId: params.clienteId,
            clienteNome: params.clienteNome,
            telefone: params.telefone,
        });
    }, [websocket]);

    const aceitarLigacao = useCallback(async (chamadaId: number, participantKey: string, livekitToken?: string, livekitUrl?: string) => {
        if (!chamadaId || !participantKey) return;

        // Usar URL do backend se fornecida, senão usar a configuração local
        const connectionUrl = livekitUrl || ligacao?.livekitUrl || 'wss://live.terachat.app';
        if (!connectionUrl) {
            console.warn('[LiveKit] URL de conexão não fornecida');
            return;
        }

        try {
            // Se não temos o token, solicitar ao backend
            if (!livekitToken) {
                console.log('[LiveKit] Solicitando token ao backend...');
                websocket.emit('call.accept', {
                    id: chamadaId,
                    participantKey,
                });
                return;
            }

            console.log('[LiveKit] Conectando à sala com token...', { connectionUrl });

            // Limpar recursos anteriores antes de criar novos
            if (audioLevelIntervalRef.current) {
                window.clearInterval(audioLevelIntervalRef.current);
                audioLevelIntervalRef.current = null;
            }

            // Limpar analisadores antigos para permitir recriação
            audioAnalyserLocalRef.current = null;
            audioAnalyserRemoteRef.current = null;

            // Fechar AudioContext antigo para evitar problemas de áudio
            if (audioContextRef.current) {
                try {
                    await audioContextRef.current.close();
                } catch (err) {
                    console.warn('[LiveKit] Erro ao fechar AudioContext anterior:', err);
                }
                audioContextRef.current = null;
            }

            // Remover elementos de áudio antigos do DOM
            const existingAudioElements = document.querySelectorAll('[id^="remote-audio-"]');
            existingAudioElements.forEach(el => {
                console.log('[LiveKit] Removendo elemento de áudio antigo:', el.id);
                el.remove();
            });

            // Desconectar room anterior se existir
            if (roomRef.current) {
                try {
                    await roomRef.current.disconnect();
                } catch (err) {
                    console.warn('[LiveKit] Erro ao desconectar room anterior:', err);
                }
                roomRef.current = null;
            }

            // Criar instância da Room
            const room = new Room({
                adaptiveStream: true,
                dynacast: true,
                audioCaptureDefaults: {
                    autoGainControl: true,
                    echoCancellation: true,
                    noiseSuppression: true,
                },
            });

            roomRef.current = room;

            // Configurar handlers de eventos
            room.on(RoomEvent.Connected, () => {
                console.log('[LiveKit] ✅ Conectado à sala');
                const now = new Date();
                setLigacaoState(prev => {
                    console.log('[LiveKit] Atualizando estado, prev:', prev);
                    if (prev) {
                        return { ...prev, status: 'em_andamento', startDate: now };
                    }
                    // Se prev é null, criar estado completo
                    return {
                        status: 'em_andamento',
                        startDate: now,
                        isMuted: false,
                        isSpeakerOn: true,
                        isMinimized: false,
                        localAudioLevel: 0,
                        remoteAudioLevel: 0,
                    };
                });
                connectionFailureCountRef.current = 0;
            });

            room.on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
                console.log('[LiveKit] Desconectado da sala:', reason);

                if (isTransferringRef.current) {
                    console.log('[LiveKit] Desconexão por transferência');
                    return;
                }

                // Tentar reconectar se não foi intencional
                if (reason !== DisconnectReason.CLIENT_INITIATED) {
                    reconnectionTimeoutRef.current = window.setTimeout(() => {
                        tentarReconectar();
                    }, 1000);
                }
            });

            room.on(RoomEvent.ConnectionStateChanged, (state: ConnectionState) => {
                console.log('[LiveKit] Estado da conexão:', state);
            });

            room.on(RoomEvent.ParticipantConnected, (participant: RemoteParticipant) => {
                console.log('[LiveKit] Participante conectado:', participant.identity);
            });

            room.on(RoomEvent.ParticipantDisconnected, (participant: RemoteParticipant) => {
                console.log('[LiveKit] Participante desconectado:', participant.identity);
            });

            room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack, publication: RemoteTrackPublication, participant: RemoteParticipant) => {
                console.log('[LiveKit] 🎵 Track recebida:', track.kind, 'de', participant.identity);

                if (track.kind === Track.Kind.Audio) {
                    // Remover elemento de áudio existente para este participante (evita duplicação)
                    const existingElement = document.getElementById(`remote-audio-${participant.identity}`);
                    if (existingElement) {
                        console.log('[LiveKit] Removendo elemento de áudio existente para:', participant.identity);
                        existingElement.remove();
                    }

                    // Anexar o elemento de áudio ao DOM para reprodução
                    const audioElement = track.attach();
                    audioElement.id = `remote-audio-${participant.identity}`;

                    // Configurar propriedades de áudio para melhor qualidade
                    audioElement.autoplay = true;
                    (audioElement as any).playsInline = true;
                    (audioElement as any).disableRemotePlayback = true;

                    document.body.appendChild(audioElement);

                    // Configurar analisadores de áudio
                    const remoteStream = new MediaStream([track.mediaStreamTrack]);
                    setupAudioAnalysers(undefined, remoteStream);
                }
            });

            room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack, publication: RemoteTrackPublication, participant: RemoteParticipant) => {
                console.log('[LiveKit] Track removida:', track.kind, 'de', participant.identity);

                if (track.kind === Track.Kind.Audio) {
                    const elements = track.detach();
                    elements.forEach((el: HTMLMediaElement) => el.remove());
                }
            });

            room.on(RoomEvent.LocalTrackPublished, (publication: LocalTrackPublication, participant: LocalParticipant) => {
                console.log('[LiveKit] Track local publicada:', publication.trackName, publication.kind);

                // Configurar analisador de áudio quando a track de microfone for publicada
                if (publication.kind === Track.Kind.Audio && publication.track) {
                    console.log('[LiveKit] 🎤 Configurando analisador de áudio local...');
                    const localStream = new MediaStream([publication.track.mediaStreamTrack]);
                    setupAudioAnalysers(localStream);
                }
            });

            // Conectar à sala
            console.log('[LiveKit] Conectando à sala...', {
                url: connectionUrl,
                tokenLength: livekitToken?.length,
                roomName: roomRef.current?.name
            });

            await room.connect(connectionUrl, livekitToken);

            console.log('[LiveKit] ✅ Conexão estabelecida', {
                localParticipant: room.localParticipant?.identity,
                roomState: room.state,
                remoteParticipants: room.remoteParticipants?.size
            });

            // Habilitar microfone (o analisador de áudio será configurado no evento LocalTrackPublished)
            // Usa retry mais agressivo para cenários de transferência onde o mic pode estar em uso por outra aba
            const maxMicRetries = 5;
            let micEnabled = false;

            for (let attempt = 1; attempt <= maxMicRetries && !micEnabled; attempt++) {
                try {
                    // Verificar permissão de microfone
                    const permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });
                    console.log(`[LiveKit] 🎤 [Tentativa ${attempt}/${maxMicRetries}] Status de permissão do microfone:`, permissionStatus.state);

                    if (permissionStatus.state === 'denied') {
                        console.error('[LiveKit] ❌ Permissão de microfone negada pelo usuário');
                        alert('Permissão de microfone negada. Por favor, permita o acesso ao microfone nas configurações do navegador.');
                        return;
                    }

                    console.log(`[LiveKit] 🎤 [Tentativa ${attempt}/${maxMicRetries}] Habilitando microfone...`);
                    await room.localParticipant.setMicrophoneEnabled(true);

                    // Verificar se realmente habilitou
                    if (room.localParticipant.isMicrophoneEnabled && room.localParticipant.audioTrackPublications.size > 0) {
                        micEnabled = true;
                        console.log('[LiveKit] 🎤 ✅ Microfone habilitado com sucesso', {
                            audioTracks: room.localParticipant.audioTrackPublications.size,
                            isMicrophoneEnabled: room.localParticipant.isMicrophoneEnabled
                        });
                    } else {
                        console.warn('[LiveKit] ⚠️ setMicrophoneEnabled retornou mas sem tracks, tentando novamente...');
                        throw new Error('Microfone habilitado mas sem tracks de áudio');
                    }
                } catch (micError: any) {
                    console.error(`[LiveKit] ❌ [Tentativa ${attempt}/${maxMicRetries}] Erro ao habilitar microfone:`, micError?.message || micError);

                    if (attempt < maxMicRetries) {
                        // Aumentar o delay progressivamente para dar tempo do outro agente liberar o mic
                        const delay = attempt * 800; // 800ms, 1600ms, 2400ms, 3200ms
                        console.log(`[LiveKit] ⏳ Aguardando ${delay}ms antes de tentar novamente...`);
                        await new Promise(r => setTimeout(r, delay));
                    } else {
                        console.error('[LiveKit] ❌ Falha definitiva ao habilitar microfone após todas as tentativas');
                        // Não bloquear a chamada - o usuário pode tentar reconectar manualmente
                    }
                }
            }

        } catch (error: any) {
            console.error('[LiveKit] Erro ao conectar:', {
                message: error?.message,
                code: error?.code,
                name: error?.name,
                stack: error?.stack?.split('\n').slice(0, 3).join('\n')
            });
            // Não tentar reconectar automaticamente - o backend/websocket vai lidar com isso
            // Se for transferência, o agente de destino vai receber um novo token
        }
    }, [websocket, setupAudioAnalysers]);

    const conectarLigacao = useCallback(async (chamadaId: number) => {
        if (!user?.id) {
            console.warn('[LiveKit] Usuário não autenticado, não é possível conectar ligação');
            return;
        }
        const participantKey = `call-${chamadaId}:agent:${user.id}`;
        aceitarLigacao(chamadaId, participantKey);
    }, [aceitarLigacao, user]);

    /**
     * Handler para quando a chamada outbound é iniciada com sucesso
     */
    const handleOutboundInitiated = useCallback(async (data: {
        callId: string;
        roomName: string;
        livekitToken: string;
        livekitUrl: string;
        chamadaId: number;
    }) => {
        if (!user?.id) {

            console.warn('[LiveKit] Usuário não autenticado, ignorando chamada outbound');
            return;
        }
        console.log('[LiveKit] 📞 Chamada outbound iniciada:', data);

        const participantKey = `call-${data.chamadaId}:agent:${user.id}`;

        setLigacao(prev => ({
            canalClienteId: prev?.canalClienteId ?? '',
            canalClienteNome: prev?.canalClienteNome ?? 'Cliente',
            ...(prev ?? {}),
            participantKey,
            roomName: data.roomName,
            livekitToken: data.livekitToken,
            livekitUrl: data.livekitUrl,
        }));

        // Conectar à sala LiveKit
        await aceitarLigacao(data.chamadaId, participantKey, data.livekitToken, data.livekitUrl);
    }, [aceitarLigacao, user?.id]);

    /**
     * Handler para erro na chamada outbound
     */
    const handleOutboundError = useCallback((data: { error: string }) => {
        console.error('[LiveKit] ❌ Erro na chamada outbound:', data.error);

        toast.error(`Erro ao realizar ligação: ${data.error}`);

        // Limpar estado da ligação
        setLigacao(null);
        setLigacaoState(null);
    }, []);

    const encerrarLigacao = useCallback(async (solucionado?: boolean, askAfter: boolean = true) => {
        // Se foi chamada com boolean, interpretar como apenas enviar solução
        if (typeof solucionado === 'boolean') {
            const pKey = lastParticipantKeyRef.current ?? ligacao?.participantKey ?? undefined;
            const cId = lastCallIdRef.current ?? ligacao?.participantKey?.split(':')[0] ?? undefined;
            if (websocket && (pKey || cId)) {
                websocket.emit('call.solution', {
                    participantKey: pKey,
                    callId: cId,
                    solucionado,
                });
            }
            setLigacao(null);
            setLigacaoState(null);
            return;
        }

        if (!ligacao) return;

        // Guardar identificadores
        const lastParticipantKey = ligacao.participantKey;
        const lastCallId = ligacao.participantKey?.split(':')[0] || null;
        lastParticipantKeyRef.current = lastParticipantKey || null;
        lastCallIdRef.current = lastCallId;

        // Limpar intervalo de análise de áudio
        if (audioLevelIntervalRef.current) {
            window.clearInterval(audioLevelIntervalRef.current);
            audioLevelIntervalRef.current = null;
        }

        // Limpar timeout de reconexão
        if (reconnectionTimeoutRef.current) {
            window.clearTimeout(reconnectionTimeoutRef.current);
            reconnectionTimeoutRef.current = null;
        }

        // Resetar flags e contadores
        connectionFailureCountRef.current = 0;
        isTransferringRef.current = false;

        // IMPORTANTE: Liberar microfone ANTES de desconectar
        await liberarMicrofone();

        // Desconectar da sala LiveKit
        if (roomRef.current) {
            try {
                await roomRef.current.disconnect();
            } catch (err) {
                console.error('[LiveKit] Erro ao desconectar:', err);
            }
            roomRef.current = null;
        }

        // Fechar AudioContext
        if (audioContextRef.current) {
            try {
                await audioContextRef.current.close();
            } catch { }
            audioContextRef.current = null;
        }

        audioAnalyserLocalRef.current = null;
        audioAnalyserRemoteRef.current = null;

        // Encerrar no servidor
        if (websocket && user) {
            websocket.emit('call.end', {
                participantKey: lastParticipantKeyRef.current,
                callId: lastCallIdRef.current,
                canalClienteId: ligacao.canalClienteId,
                id: user.id,
                nome: user.nome,
                imagem: user.imagem,
            });
        }

        if (askAfter) {
            setLigacaoState(prev => prev ? { ...prev, status: 'finalizado' } : {
                status: 'finalizado',
                startDate: null,
                isMuted: false,
                isSpeakerOn: true,
                isMinimized: false,
                localAudioLevel: 0,
                remoteAudioLevel: 0,
            });
        } else {
            setLigacao(null);
            setLigacaoState(null);
        }
    }, [ligacao, websocket, user]);

    const toggleMute = useCallback(async () => {
        if (!roomRef.current) return;

        const currentMuted = ligacaoState?.isMuted ?? false;
        const newMuted = !currentMuted;

        try {
            await roomRef.current.localParticipant.setMicrophoneEnabled(!newMuted);
            setLigacaoState(prev => prev ? { ...prev, isMuted: newMuted } : null);

            // Notificar servidor
            if (ligacao?.participantKey) {
                websocket.emit('call.mute', { participantKey: ligacao.participantKey, muted: newMuted });
            }
        } catch (error) {
            console.error('[LiveKit] Erro ao mutar/desmutar:', error);
        }
    }, [ligacao, ligacaoState, websocket]);

    const toggleSpeaker = useCallback(() => {
        setLigacaoState(prev => {
            if (!prev) return null;

            // No web, podemos ajustar o volume dos elementos de áudio remotos
            const audioElements = document.querySelectorAll('[id^="remote-audio-"]') as NodeListOf<HTMLAudioElement>;
            audioElements.forEach(el => {
                el.volume = prev.isSpeakerOn ? 0 : 1;
            });

            return { ...prev, isSpeakerOn: !prev.isSpeakerOn };
        });
    }, []);

    const toggleMinimized = useCallback(() => {
        setLigacaoState(prev => {
            if (!prev) return null;
            return { ...prev, isMinimized: !prev.isMinimized };
        });
    }, []);

    const transferirLigacao = useCallback(async (targetUserId?: number, targetDepartmentId?: number) => {
        if (!ligacao?.participantKey) {
            throw new Error('Nenhuma ligação ativa para transferir');
        }

        if (!targetUserId && !targetDepartmentId) {
            throw new Error('É necessário especificar um usuário ou departamento de destino');
        }

        isTransferringRef.current = true;

        // IMPORTANTE: Liberar microfone ANTES de enviar a transferência
        // para evitar conflito com o agente de destino
        await liberarMicrofone();

        websocket.emit('call.transfer', {
            participantKey: ligacao.participantKey,
            targetUserId,
            targetDepartmentId
        });
    }, [ligacao, websocket, liberarMicrofone]);

    const tentarReconectar = useCallback(async () => {
        // Não tentar reconectar - o token pode estar inválido
        // Se necessário, uma nova chamada será iniciada pelo backend
        console.log('[LiveKit] Reconexão automática desabilitada - aguardando nova solicitação do backend');
        return;

        // Código original comentado:
        // if (!ligacao?.participantKey || !ligacao?.livekitToken || isTransferringRef.current) {
        //     return;
        // }
        //
        // const [callId] = ligacao.participantKey.split(':');
        //
        // try {
        //     console.log('[LiveKit] Tentando reconectar...');
        //
        //     if (reconnectionTimeoutRef.current) {
        //         window.clearTimeout(reconnectionTimeoutRef.current);
        //         reconnectionTimeoutRef.current = null;
        //     }
        //
        //     await aceitarLigacao(parseInt(callId.replace('call-', '')), ligacao.participantKey, ligacao.livekitToken);
        //     connectionFailureCountRef.current = 0;
        // } catch (error) {
        //     console.error('[LiveKit] Erro na reconexão:', error);
        //     connectionFailureCountRef.current++;
        //
        //     if (connectionFailureCountRef.current >= 3) {
        //         console.log('[LiveKit] Muitas tentativas de reconexão falharam, encerrando ligação');
        //         encerrarLigacao(undefined, false);
        //     } else {
        //         reconnectionTimeoutRef.current = window.setTimeout(() => {
        //             tentarReconectar();
        //         }, 2000 * connectionFailureCountRef.current);
        //     }
        // }
    }, [ligacao, aceitarLigacao, encerrarLigacao]);

    const dStatus: Record<string, LigacaoStatus> = {
        'inc-wait-agent': 'chamando',
        'out-wait-client': 'chamando',
        'connected': 'em_andamento',
        'ended': 'finalizado',
        'failed': 'finalizado',
        'rejected': 'finalizado'
    };

    const handleParams = useCallback((params: any) => {
        if (!user?.id) {
            console.warn('[LiveKit] Usuário não autenticado, ignorando params');
            return;
        }
        if (params.chamadasAtivas && Array.isArray(params.chamadasAtivas)) {
            console.log('Chamadas ativas do usuário:', params.chamadasAtivas);

            const chamada = params.chamadasAtivas[0];
            if (chamada) {
                const participantKey = `${chamada.callId}:agent:${user.id}`;
                setLigacao({
                    participantKey,
                    roomName: chamada.roomName,
                    livekitToken: chamada.livekitToken,
                    livekitUrl: chamada.livekitUrl,
                    canalClienteId: chamada.client?.id || '',
                    canalClienteNome: chamada.client?.name || 'Cliente',
                });
                setLigacaoState(_newLigacaoState(dStatus[chamada.state] as LigacaoStatus));

                if (chamada.livekitToken && (chamada.state === 'connected' || chamada.state === 'inc-wait-agent')) {
                    aceitarLigacao(chamada.chamadaId, participantKey, chamada.livekitToken, chamada.livekitUrl);
                }
            } else {
                setLigacao(null);
                setLigacaoState(null);
            }
        }
    }, [_newLigacaoState, aceitarLigacao, user]);

    const handleCallIncoming = useCallback(async (data: {
        callId: string;
        canalClienteId: string;
        clienteNome: string;
        roomName?: string;
    }) => {
        if (!user?.id) {
            console.warn('[LiveKit] Usuário não autenticado, ignorando chamada incoming');
            return;
        }
        setLigacao({
            participantKey: `${data.callId}:agent:${user.id}`,
            roomName: data.roomName,
            canalClienteId: data.canalClienteId,
            canalClienteNome: data.clienteNome,
        });

        setLigacaoState(_newLigacaoState('chamando'));
    }, [_newLigacaoState, user]);

    const handleCallAccepted = useCallback(async (data: {
        participantKey: string;
        livekitToken: string;
        livekitUrl?: string;
        roomName: string;
        canalClienteId: string;
        canalClienteNome: string;
    }) => {
        try {
            console.log('[LiveKit] Token recebido do servidor', data);

            setLigacao(prev => ({
                ...(prev ?? {}),
                participantKey: data.participantKey,
                livekitToken: data.livekitToken,
                livekitUrl: data.livekitUrl,
                roomName: data.roomName,
                canalClienteId: data.canalClienteId,
                canalClienteNome: data.canalClienteNome
            }));

            // Garantir que ligacaoState existe (pode ser uma transferência onde o estado era null)
            setLigacaoState(prev => prev ?? _newLigacaoState('iniciando'));

            // Extrair chamadaId do participantKey
            const [callIdPart] = data.participantKey.split(':');
            const chamadaId = parseInt(callIdPart.replace('call-', ''));

            // Conectar com o token recebido (usar URL do backend se fornecida)
            await aceitarLigacao(chamadaId, data.participantKey, data.livekitToken, data.livekitUrl);

        } catch (error) {
            console.error('[LiveKit] Erro ao processar resposta:', error);
        }
    }, [aceitarLigacao, _newLigacaoState]);

    /**
     * Handler para quando o servidor envia call.accept.error (cliente já desligou ou erro ao aceitar)
     */
    const handleCallAcceptError = useCallback((data: { callId: string; error: string }) => {
        console.log('[LiveKit] ❌ Erro ao aceitar chamada:', data);

        // Mensagem amigável para o usuário
        const errorMessage = data.error === 'Cliente já desligou a chamada'
            ? 'Cliente já desligou a chamada'
            : `Erro ao aceitar chamada: ${data.error}`;

        toast.error(errorMessage);

        // Limpar estado da ligação se houver
        setLigacao(null);
        setLigacaoState(null);
    }, []);

    const handleTransferSuccess = useCallback(async () => {
        console.log('[LiveKit] handleTransferSuccess - Transferência bem-sucedida, desconectando...');
        isTransferringRef.current = false;

        // Desconectar da sala (microfone já foi liberado em transferirLigacao)
        if (roomRef.current) {
            try {
                // Garantir liberação do microfone caso ainda não tenha sido feito
                await liberarMicrofone();

                console.log('[LiveKit] Desconectando room após transferência...');
                await roomRef.current.disconnect();
                console.log('[LiveKit] Room desconectado com sucesso');
            } catch (err) {
                console.warn('[LiveKit] Erro ao desconectar room após transferência:', err);
            }
            roomRef.current = null;
        }

        setLigacao(null);
        setLigacaoState(null);

        if (audioContextRef.current) {
            try {
                await audioContextRef.current.close();
            } catch { }
            audioContextRef.current = null;
        }

        audioAnalyserLocalRef.current = null;
        audioAnalyserRemoteRef.current = null;

        if (audioLevelIntervalRef.current) {
            window.clearInterval(audioLevelIntervalRef.current);
            audioLevelIntervalRef.current = null;
        }

        if (reconnectionTimeoutRef.current) {
            window.clearTimeout(reconnectionTimeoutRef.current);
            reconnectionTimeoutRef.current = null;
        }

        connectionFailureCountRef.current = 0;
    }, [liberarMicrofone]);

    const handleTransferError = useCallback((data: { error: string }) => {
        console.error('Erro na transferência:', data.error);
        isTransferringRef.current = false;
    }, []);

    const handleCallMuted = useCallback((data: { muted: boolean }) => {
        console.log('[LiveKit] Call muted:', data.muted);
        setLigacaoState(prev => prev ? { ...prev, isMuted: data.muted } : prev);
    }, []);

    /**
     * Handler para quando o servidor envia call.end (cliente WhatsApp desligou ou chamada encerrada remotamente)
     * Diferente de encerrarLigacao, este não emite call.end de volta para o servidor
     */
    const handleCallEnd = useCallback(async (data?: { id?: number; reason?: string }) => {
        console.log('[LiveKit] 📞 Recebido call.end do servidor:', data);

        // Se a chamada foi encerrada pelo cliente (WhatsApp desligou)
        if (data?.reason === 'client_hangup') {
            console.log('[LiveKit] 📞 Cliente WhatsApp desligou a chamada');
        }

        // Se a chamada foi transferida para outro atendente
        if (data?.reason === 'transfer') {
            console.log('[LiveKit] 📞 Chamada transferida para outro atendente');
        }

        // Limpar intervalo de análise de áudio
        if (audioLevelIntervalRef.current) {
            window.clearInterval(audioLevelIntervalRef.current);
            audioLevelIntervalRef.current = null;
        }

        // Limpar timeout de reconexão
        if (reconnectionTimeoutRef.current) {
            window.clearTimeout(reconnectionTimeoutRef.current);
            reconnectionTimeoutRef.current = null;
        }

        // Resetar flags e contadores
        connectionFailureCountRef.current = 0;
        isTransferringRef.current = false;

        // Liberar microfone
        await liberarMicrofone();

        // Desconectar da sala LiveKit
        if (roomRef.current) {
            try {
                await roomRef.current.disconnect();
            } catch (err) {
                console.error('[LiveKit] Erro ao desconectar:', err);
            }
            roomRef.current = null;
        }

        // Fechar AudioContext
        if (audioContextRef.current) {
            try {
                await audioContextRef.current.close();
            } catch { }
            audioContextRef.current = null;
        }

        audioAnalyserLocalRef.current = null;
        audioAnalyserRemoteRef.current = null;

        // Limpar estado da ligação
        setLigacao(null);
        setLigacaoState(null);

        console.log('[LiveKit] ✅ Ligação encerrada (notificação do servidor)');
    }, [liberarMicrofone]);

    // Registrar eventos do websocket
    useEffect(() => {
        websocket.on('call.incoming', handleCallIncoming);
        websocket.on('call.accepted', handleCallAccepted);
        websocket.on('call.accept.error', handleCallAcceptError);
        websocket.on('call.transfer.success', handleTransferSuccess);
        websocket.on('call.transfer.error', handleTransferError);
        websocket.on('call.muted', handleCallMuted);
        websocket.on('call.end', handleCallEnd);
        websocket.on('call.outbound.initiated', handleOutboundInitiated);
        websocket.on('call.outbound.error', handleOutboundError);
        websocket.on('params', handleParams);

        return () => {
            websocket.off('call.incoming', handleCallIncoming);
            websocket.off('call.accepted', handleCallAccepted);
            websocket.off('call.accept.error', handleCallAcceptError);
            websocket.off('call.transfer.success', handleTransferSuccess);
            websocket.off('call.transfer.error', handleTransferError);
            websocket.off('call.muted', handleCallMuted);
            websocket.off('call.end', handleCallEnd);
            websocket.off('call.outbound.initiated', handleOutboundInitiated);
            websocket.off('call.outbound.error', handleOutboundError);
            websocket.off('params', handleParams);
        };
    }, [websocket, handleCallIncoming, handleCallAccepted, handleCallAcceptError, handleTransferSuccess, handleTransferError, handleParams, handleCallEnd, handleCallMuted, handleOutboundInitiated, handleOutboundError]);

    // Cleanup ao desmontar
    useEffect(() => {
        return () => {
            if (roomRef.current) {
                roomRef.current.disconnect();
            }
            if (audioContextRef.current) {
                audioContextRef.current.close();
            }
            if (audioLevelIntervalRef.current) {
                window.clearInterval(audioLevelIntervalRef.current);
            }
            if (reconnectionTimeoutRef.current) {
                window.clearTimeout(reconnectionTimeoutRef.current);
            }
        };
    }, []);

    return {
        ligacao,
        ligacaoState,
        iniciarLigacao,
        iniciarChamadaOutbound,
        aceitarLigacao,
        conectarLigacao,
        encerrarLigacao,
        transferirLigacao,
        toggleMute,
        toggleSpeaker,
        toggleMinimized
    };
}
